// Developer note: install icons package before running the app.
// npm:    npm install @ant-design/icons
// yarn:   yarn add @ant-design/icons
// pnpm:   pnpm add @ant-design/icons

import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  ConfigProvider, Form, Input, Select, Button, Card,
  Row, Col, Typography, Upload, Checkbox, Divider,
  Tag, List, Avatar, Space, message
} from 'antd';
import {
  PlusOutlined, UploadOutlined, DeleteOutlined,
  SaveOutlined, CloseOutlined, BankOutlined,
  EnvironmentOutlined, ThunderboltOutlined,
  ReadOutlined, SafetyCertificateOutlined,
  GlobalOutlined, RocketOutlined, FilePdfOutlined,
  PictureOutlined, TrophyOutlined, FileTextOutlined,
  CameraOutlined, LinkOutlined, ToolOutlined, CarOutlined
} from '@ant-design/icons';
import { Users } from 'lucide-react';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { Option } = Select;

// Shared layout constants for consistent spacing
const GUTTER = 16;

// Refined Section Header (compact, neutral)
const SectionHeader = ({ icon, title }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
    <span style={{ color: '#5b2a86', fontSize: 18 }}>{icon}</span>
    <Typography.Title level={5} style={{ margin: 0, color: '#141414', fontWeight: 600 }}>{title}</Typography.Title>
  </div>
);

// --- Main Component ---

const AddDistrictForm = ({ onSave, editingDistrict, onCancel, setSubmitHandler }) => {
  const [form] = Form.useForm();
  
  // --- State Management ---
  const initialState = {
    districtName: '', coverImage: null, aboutDistrict: '', districtWebsite: '',
    industrialZone: 'D+', odopProduct: '', msmeCount: '', largeIndustriesCount: '', majorIndustries: '',
    minerals: '', agricultureProducts: '', forestResources: '', waterResources: '',
    midcZones: [], infrastructureDetails: '', highways: '', railways: 'None',
    nearestAirport: '', nearestPort: '', connectivityDetails: '',
    powerAvailability: 'Adequate', powerDetails: '', waterDetails: '',
    gasInfrastructure: '', fireSafetyProfile: '', skilledLabor: '', educationDetails: '',
    landSubsidy: false, stampDutyExemption: false, powerSubsidy: false, taxHoliday: false, otherIncentives: '',
    investmentOpportunities: [], industrialGrowthRate: '',
    policies: [], schemes: [], mediaCoverage: [], pressReleases: [], chairCoChairs: [], awards: [], resources: [],
    latitude: 21.1458,
    longitude: 79.0882,
    odopProductImage: null,
  };

  const [formData, setFormData] = useState(initialState);
  const [newCoverImage, setNewCoverImage] = useState(null);
  const [imageFileList, setImageFileList] = useState([]); // For ODOP product image
  const [odopImageFileList, setOdopImageFileList] = useState([]); // For ODOP product image

  // Temporary States
  const [tempOpp, setTempOpp] = useState({ sector: '', rationale: '' });
  const [tempPolicy, setTempPolicy] = useState({ name: '', description: '', date: '', file: null });
  const [tempScheme, setTempScheme] = useState({ name: '', description: '', date: '', file: null });
  const [tempMedia, setTempMedia] = useState({ title: '', description: '', date: '', sourceName: '', sourceUrl: '', image: null });
  const [tempPress, setTempPress] = useState({ title: '', description: '', date: '', source: '', file: null });
  const [tempChair, setTempChair] = useState({ name: '', description: '', postName: '', role: 'Convener', photo: null });
  const [tempAward, setTempAward] = useState({ awardName: '', description: '', organization: '', category: 'Industrial Development', date: '', certificate: null });
  const [tempResource, setTempResource] = useState({ name: '', description: '', date: '', document: null });
  const [tempMidc, setTempMidc] = useState('');

  // Track if data has been loaded to prevent infinite loops
  const [dataLoaded, setDataLoaded] = useState(false);

  // Track the district ID to prevent reloading same district
  const loadedDistrictId = useRef(null);

  // Memoize the district ID to prevent unnecessary rerenders
  const currentDistrictId = useMemo(() => editingDistrict?._id, [editingDistrict?._id]);

  // FIXED: Effect to load data if editing - prevent infinite loop
  useEffect(() => {
    //console.log('=== useEffect triggered ===');
    
    if (currentDistrictId && currentDistrictId !== loadedDistrictId.current) {
      //console.log('Loading district:', currentDistrictId);
      //console.log('District data:', editingDistrict);
      
      const loadedData = {
        districtName: editingDistrict.districtName || '',
        coverImage: editingDistrict.coverImage || null,
        aboutDistrict: editingDistrict.aboutDistrict || '',
        districtWebsite: editingDistrict.districtWebsite || '',
        industrialZone: editingDistrict.industrialZone || 'D',
        odopProduct: editingDistrict.odopProduct || '',
        msmeCount: String(editingDistrict.msmeCount || ''),
        largeIndustriesCount: String(editingDistrict.largeIndustriesCount || ''),
        majorIndustries: editingDistrict.majorIndustries || '',
        industrialGrowthRate: String(editingDistrict.industrialGrowthRate || ''),
        minerals: editingDistrict.minerals || '',
        agricultureProducts: editingDistrict.agricultureProducts || '',
        forestResources: editingDistrict.forestResources || '',
        waterResources: editingDistrict.waterResources || '',
        midcZones: Array.isArray(editingDistrict.midcZones) ? [...editingDistrict.midcZones] : [],
        infrastructureDetails: editingDistrict.infrastructureDetails || '',
        highways: editingDistrict.highways || '',
        railways: editingDistrict.railways || 'None',
        nearestAirport: editingDistrict.nearestAirport || '',
        nearestPort: editingDistrict.nearestPort || '',
        connectivityDetails: editingDistrict.connectivityDetails || '',
        powerAvailability: editingDistrict.powerAvailability || 'Adequate',
        powerDetails: editingDistrict.powerDetails || '',
        waterDetails: editingDistrict.waterDetails || '',
        gasInfrastructure: editingDistrict.gasInfrastructure || '',
        fireSafetyProfile: editingDistrict.fireSafetyProfile || '',
        skilledLabor: editingDistrict.skilledLabor || '',
        educationDetails: editingDistrict.educationDetails || '',
        landSubsidy: Boolean(editingDistrict.landSubsidy),
        stampDutyExemption: Boolean(editingDistrict.stampDutyExemption),
        powerSubsidy: Boolean(editingDistrict.powerSubsidy),
        taxHoliday: Boolean(editingDistrict.taxHoliday),
        otherIncentives: editingDistrict.otherIncentives || '',
        investmentOpportunities: Array.isArray(editingDistrict.investmentOpportunities) ? [...editingDistrict.investmentOpportunities] : [],
        policies: Array.isArray(editingDistrict.policies) ? [...editingDistrict.policies] : [],
        schemes: Array.isArray(editingDistrict.schemes) ? [...editingDistrict.schemes] : [],
        mediaCoverage: Array.isArray(editingDistrict.mediaCoverage) ? [...editingDistrict.mediaCoverage] : [],
        pressReleases: Array.isArray(editingDistrict.pressReleases) ? [...editingDistrict.pressReleases] : [],
        chairCoChairs: Array.isArray(editingDistrict.chairCoChairs) ? [...editingDistrict.chairCoChairs] : [],
        awards: Array.isArray(editingDistrict.awards) ? [...editingDistrict.awards] : [],
        resources: Array.isArray(editingDistrict.resources) ? [...editingDistrict.resources] : [],
        latitude: editingDistrict.latitude || 21.1458,
        longitude: editingDistrict.longitude || 79.0882,
        odopProductImage: editingDistrict.odopProductImage || null,
      };

      // Load ODOP image if exists
      if (editingDistrict.odopProductImage) {
        setOdopImageFileList([{
          uid: '-1',
          name: 'ODOP Product Image',
          status: 'done',
          url: `${process.env.REACT_APP_API_BASE_URL}/uploads/${editingDistrict.odopProductImage}`
        }]);
      }

      //console.log('Loaded data:', loadedData);
      
      // Update both state and form
      setFormData(loadedData);
      form.setFieldsValue(loadedData); // Sync with Ant Design Form
      setNewCoverImage(null);
      loadedDistrictId.current = currentDistrictId;
      
      // Log specific fields to verify
      // console.log('Verifying fields:', {
      //   districtName: loadedData.districtName,
      //   industrialZone: loadedData.industrialZone,
      //   railways: loadedData.railways,
      //   powerAvailability: loadedData.powerAvailability
      // });
      
      message.success({
        content: `Loaded "${editingDistrict.districtName}" for editing`,
        duration: 2,
        key: 'district-loaded'
      });
    } else if (!currentDistrictId && loadedDistrictId.current) {
      // console.log('Resetting form for new district');
      setFormData(initialState);
      form.resetFields();
      setNewCoverImage(null);
      loadedDistrictId.current = null;
    }
  }, [currentDistrictId, form, editingDistrict]);

  // Remove the formData debug useEffect or make it conditional
  useEffect(() => {
    if (dataLoaded) {
      // //console.log('FormData loaded:', {
      //   districtName: formData.districtName,
      //   industrialZone: formData.industrialZone,
      //   msmeCount: formData.msmeCount
      // });
    }
  }, [dataLoaded]); // Only log once after loading

  useEffect(() => {
    // expose submit to parent (Modal footer)
    if (typeof setSubmitHandler === 'function') {
      setSubmitHandler(() => handleSubmit);
    }
  }, [setSubmitHandler, formData, newCoverImage]);

  // --- Handlers ---

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Generic File Handler for AntD Upload (mocking manual request)
  const handleFileChange = (info, setter, isMainCover = false) => {
    const file = info.file.originFileObj || info.file;
    
    // Additional validation
    const fileName = file.name.toLowerCase();
    const validExtensions = ['.jpg', '.jpeg', '.png', '.pdf'];
    const hasValidExtension = validExtensions.some(ext => fileName.endsWith(ext));
    
    if (!hasValidExtension) {
      message.error(`Invalid file: ${file.name}. Only JPG, PNG, and PDF files are allowed.`);
      return false;
    }
    
    //console.log('File selected:', file.name, 'Type:', file.type, 'Size:', (file.size / 1024).toFixed(2) + ' KB');
    
    if (isMainCover) setNewCoverImage(file);
    else setter(file);
    return false; // Prevent auto upload
  };

  // ODOP Product Image Upload
  const handleImageUpload = ({ fileList }) => {
    setImageFileList(fileList);
  };

  const beforeImageUpload = (file) => {
    const isImage = file.type.startsWith('image/');
    if (!isImage) {
      message.error('You can only upload image files!');
      return false;
    }
    const isLt5M = file.size / 1024 / 1024 < 5;
    if (!isLt5M) {
      message.error('Image must be smaller than 5MB!');
      return false;
    }
    return false; // Prevent auto upload
  };

  // ODOP Product Image Upload
  const handleOdopImageUpload = ({ fileList }) => {
    setOdopImageFileList(fileList);
  };

  const beforeOdopImageUpload = (file) => {
    const isImage = file.type.startsWith('image/');
    if (!isImage) {
      message.error('You can only upload image files!');
      return false;
    }
    const isLt5M = file.size / 1024 / 1024 < 5;
    if (!isLt5M) {
      message.error('Image must be smaller than 5MB!');
      return false;
    }
    return false; // Prevent auto upload
  };

  // Dynamic Lists Handlers
  const addMidcZone = () => {
    if (tempMidc.trim()) {
      setFormData(prev => ({ ...prev, midcZones: [...prev.midcZones, tempMidc.trim()] }));
      setTempMidc('');
    }
  };
  const removeMidcZone = (index) => setFormData(prev => ({ ...prev, midcZones: prev.midcZones.filter((_, i) => i !== index) }));

  const addOpportunity = () => {
    if (tempOpp.sector && tempOpp.rationale) {
      setFormData(prev => ({ ...prev, investmentOpportunities: [...prev.investmentOpportunities, tempOpp] }));
      setTempOpp({ sector: '', rationale: '' });
    } else {
      message.warning("Please fill in both Sector and Rationale for the opportunity.");
    }
  };
  const removeOpportunity = (index) => setFormData(prev => ({ ...prev, investmentOpportunities: prev.investmentOpportunities.filter((_, i) => i !== index) }));

  // Generic Add/Remove for objects
  const addItem = (tempState, setTempState, listName, resetState) => {
    // Basic validation check (first property)
    if (Object.values(tempState)[0]) {
      setFormData(prev => ({ ...prev, [listName]: [...prev[listName], { ...tempState }] }));
      setTempState(resetState);
      message.success(`${listName.slice(0, -1).toUpperCase()} added successfully!`);
    } else {
      message.warning("Please fill in the required fields before adding.");
    }
  };
  const removeItem = (listName, index) => setFormData(prev => ({ ...prev, [listName]: prev[listName].filter((_, i) => i !== index) }));

  const handleSubmit = () => {
    // Basic validation check
    if (!formData.districtName || !formData.aboutDistrict) {
      message.error("District Name and About District are required fields.");
      return;
    }

    const submission = new FormData();

    // Create a clean copy of formData without File objects
    const cleanFormData = { ...formData };
    
    // Remove file references from the data object
    if (cleanFormData.policies) {
      cleanFormData.policies = cleanFormData.policies.map(p => ({
        name: p.name,
        description: p.description,
        date: p.date,
        file: p.file instanceof File ? null : p.file // Keep filename string, remove File object
      }));
    }
    
    if (cleanFormData.schemes) {
      cleanFormData.schemes = cleanFormData.schemes.map(s => ({
        name: s.name,
        description: s.description,
        date: s.date,
        file: s.file instanceof File ? null : s.file
      }));
    }
    
    if (cleanFormData.mediaCoverage) {
      cleanFormData.mediaCoverage = cleanFormData.mediaCoverage.map(m => ({
        title: m.title,
        description: m.description,
        date: m.date,
        sourceName: m.sourceName,
        sourceUrl: m.sourceUrl,
        image: m.image instanceof File ? null : m.image
      }));
    }
    
    if (cleanFormData.pressReleases) {
      cleanFormData.pressReleases = cleanFormData.pressReleases.map(p => ({
        title: p.title,
        description: p.description,
        date: p.date,
        source: p.source,
        file: p.file instanceof File ? null : p.file
      }));
    }
    
    if (cleanFormData.chairCoChairs) {
      cleanFormData.chairCoChairs = cleanFormData.chairCoChairs.map(c => ({
        name: c.name,
        description: c.description,
        postName: c.postName,
        role: c.role,
        photo: c.photo instanceof File ? null : c.photo
      }));
    }
    
    if (cleanFormData.awards) {
      cleanFormData.awards = cleanFormData.awards.map(a => ({
        awardName: a.awardName,
        description: a.description,
        organization: a.organization,
        category: a.category,
        date: a.date,
        certificate: a.certificate instanceof File ? null : a.certificate
      }));
    }
    
    if (cleanFormData.resources) {
      cleanFormData.resources = cleanFormData.resources.map(r => ({
        name: r.name,
        description: r.description,
        date: r.date,
        document: r.document instanceof File ? null : r.document
      }));
    }

    // When editing, include the district ID
    if (editingDistrict && editingDistrict._id) {
      submission.append('districtId', editingDistrict._id);
    }

    // Append main form data as JSON (without File objects)
    submission.append('districtData', JSON.stringify(cleanFormData));

    // Only append new cover image if one was selected
    if (newCoverImage && newCoverImage instanceof File) {
      submission.append('coverImage', newCoverImage);
    }

    // Append ODOP product image only if it's a new file
    if (odopImageFileList.length > 0 && odopImageFileList[0].originFileObj) {
      submission.append('odopProductImage', odopImageFileList[0].originFileObj);
    }

    // Append policy files (only new uploads)
    formData.policies.forEach((policy, index) => {
      if (policy.file && policy.file instanceof File) {
        submission.append(`policy_file_${index}`, policy.file);
      }
    });

    // Append scheme files (only new uploads)
    formData.schemes.forEach((scheme, index) => {
      if (scheme.file && scheme.file instanceof File) {
        submission.append(`scheme_file_${index}`, scheme.file);
      }
    });

    // Append media coverage images (only new uploads)
    formData.mediaCoverage.forEach((media, index) => {
      if (media.image && media.image instanceof File) {
        submission.append(`media_image_${index}`, media.image);
      }
    });

    // Append press release files (only new uploads)
    formData.pressReleases.forEach((press, index) => {
      if (press.file && press.file instanceof File) {
        submission.append(`press_file_${index}`, press.file);
      }
    });

    // Append chair/co-chair photos (only new uploads)
    formData.chairCoChairs.forEach((chair, index) => {
      if (chair.photo && chair.photo instanceof File) {
        submission.append(`chair_photo_${index}`, chair.photo);
      }
    });

    // Append award certificates (only new uploads)
    formData.awards.forEach((award, index) => {
      if (award.certificate && award.certificate instanceof File) {
        submission.append(`award_cert_${index}`, award.certificate);
      }
    });

    // Append resource documents (only new uploads)
    formData.resources.forEach((resource, index) => {
      if (resource.document && resource.document instanceof File) {
        submission.append(`resource_doc_${index}`, resource.document);
      }
    });

    // DEBUG: Log what we're sending
    // console.log('=== FormData Debug ===');
    // console.log('Mode:', editingDistrict ? 'EDIT' : 'CREATE');
    // console.log('District Name:', formData.districtName);
    // console.log('District ID:', editingDistrict?._id);
    
    const fileCount = (newCoverImage instanceof File ? 1 : 0) +
      (odopImageFileList.length > 0 && odopImageFileList[0].originFileObj ? 1 : 0) +
      formData.policies.filter(p => p.file instanceof File).length +
      formData.schemes.filter(s => s.file instanceof File).length +
      formData.mediaCoverage.filter(m => m.image instanceof File).length +
      formData.pressReleases.filter(p => p.file instanceof File).length +
      formData.chairCoChairs.filter(c => c.photo instanceof File).length +
      formData.awards.filter(a => a.certificate instanceof File).length +
      formData.resources.filter(r => r.document instanceof File).length;
    
    // console.log('Total new files to upload:', fileCount);
    // console.log('======================');

    // Call parent save handler with FormData
    onSave(submission);

    const actionText = editingDistrict ? 'updated' : 'created';
    message.success(`District data prepared for ${actionText}!`);
  };

  // --- UI Components & Layout ---

  return (
    <ConfigProvider
      componentSize="middle"
      theme={{
        token: {
          colorPrimary: '#470172', // unified primary color
          colorText: '#141414',
          colorTextSecondary: '#595959',
          colorBgLayout: '#f6f7fb',
          fontFamily: 'Inter, Rubik, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif',
          fontSize: 14,
          borderRadius: 8
        },
        components: {
          Button: { controlHeight: 36, fontWeight: 600 },
          Input: { controlHeight: 36, activeBorderColor: '#470172' },
          Select: { controlHeight: 36 },
          Card: { headerFontSize: 16, paddingSM: 18 },
          Form: { itemMarginBottom: 14, labelFontSize: 14, labelColor: '#595959' },
          List: { itemPaddingSM: '12px 16px' }
        }
      }}
    >
      {/* Layout container */}
      <div style={{ backgroundColor: '#f6f7fb', minHeight: '100vh', paddingBottom: 88 }}>
        <div style={{ maxWidth: 1440, margin: '24px auto', padding: '0 24px' }}>
          {/* Add global style to widen AntD modal */}
          <style>
            {`
              .ant-modal {
                max-width: 90vw;
              }
              .ant-modal .ant-modal-content {
                width: 960px;
                max-width: 90vw;
              }
            `}
          </style>
          {/* <Title level={3} style={{ color: '#1f1f1f', marginBottom: 14, fontWeight: 700 }}>{editingDistrict ? 'Edit District Profile' : 'Add New District Profile'}</Title> */}
          <Form 
            form={form} 
            layout="vertical" 
            requiredMark="optional"
            initialValues={formData}
          >

            {/* SECTION 1: Basic Information */}
            <Card bordered style={{ marginBottom: 18 }} headStyle={{ fontWeight: 600 }}>
              <SectionHeader icon={<BankOutlined />} title="Basic Profile" />
              <Row gutter={GUTTER}>
                <Col span={8}>
                  <Form.Item label="District Name" required name="districtName">
                    <Input name="districtName" value={formData.districtName} onChange={handleInputChange} placeholder="e.g. Nagpur" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item label="Official Website" name="districtWebsite">
                    <Input prefix={<GlobalOutlined style={{ color: '#bfbfbf' }} />} name="districtWebsite" value={formData.districtWebsite} onChange={handleInputChange} placeholder="https://..." />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item label="Cover Image" name="coverImage">
                    <Upload
                      beforeUpload={(file) => {
                        // Validate file size (5MB)
                        const isLt5M = file.size / 1024 / 1024 < 5;
                        if (!isLt5M) {
                          message.error('Image must be smaller than 5MB!');
                          return false;
                        }
                        // Validate file type
                        const isImage = file.type === 'image/jpeg' || file.type === 'image/png' || file.type === 'image/jpg';
                        if (!isImage) {
                          message.error('You can only upload JPG/PNG files!');
                          return false;
                        }
                        return handleFileChange({ file }, null, true);
                      }}
                      maxCount={1}
                      showUploadList={false}
                      accept="image/jpeg,image/png,image/jpg"
                    >
                      <Button block icon={<UploadOutlined />}>
                        {newCoverImage ? newCoverImage.name : 'Select Cover Image'}
                      </Button>
                    </Upload>
                    <Text type="secondary" style={{ fontSize: 12, marginTop: 4, display: 'block' }}>
                      Recommended: 1920x1080px (16:9), Max 5MB (JPG/PNG)
                    </Text>
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item 
                    label="Latitude" 
                    name="latitude"
                    rules={[
                      { required: true, message: 'Latitude is required' },
                      { pattern: /^-?([0-8]?[0-9]|90)(\.[0-9]{1,10})?$/, message: 'Invalid latitude' }
                    ]}
                    tooltip="Enter latitude for map positioning (e.g., 21.1458)"
                  >
                    <Input 
                      name="latitude" 
                      value={formData.latitude} 
                      onChange={handleInputChange} 
                      placeholder="21.1458"
                      type="number"
                      step="0.0001"
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item 
                    label="Longitude" 
                    name="longitude"
                    rules={[
                      { required: true, message: 'Longitude is required' },
                      { pattern: /^-?((1[0-7][0-9])|([0-9]?[0-9]))(\.[0-9]{1,10})?$/, message: 'Invalid longitude' }
                    ]}
                    tooltip="Enter longitude for map positioning (e.g., 79.0882)"
                  >
                    <Input 
                      name="longitude" 
                      value={formData.longitude} 
                      onChange={handleInputChange} 
                      placeholder="79.0882"
                      type="number"
                      step="0.0001"
                    />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item label="About District" required name="aboutDistrict">
                    <TextArea rows={4} name="aboutDistrict" value={formData.aboutDistrict} onChange={handleInputChange} showCount maxLength={800} placeholder="A brief, compelling description of the district and its unique features..." />
                  </Form.Item>
                </Col>
              </Row>
            </Card>

            {/* SECTION 2: Industrial Classification & Growth */}
            <Card bordered style={{ marginBottom: 18 }} headStyle={{ fontWeight: 600 }}>
              <SectionHeader icon={<RocketOutlined />} title="Industrial Profile & Growth" />
              <Row gutter={GUTTER} wrap align="top">
                <Col xs={24} sm={12} md={6}>
                  <Form.Item label="Industrial Zone" name="industrialZone">
                    <Select value={formData.industrialZone} onChange={(v) => handleSelectChange('industrialZone', v)}>
                      {['A', 'B', 'C', 'D', 'D+'].map(z => <Option key={z} value={z}>Zone {z}</Option>)}
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Form.Item label="ODOP Product" name="odopProduct">
                    <Input 
                      name="odopProduct" 
                      value={formData.odopProduct} 
                      onChange={handleInputChange} 
                      placeholder="One District One Product" 
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Form.Item label="MSME Count" name="msmeCount">
                    <Input type="number" name="msmeCount" value={formData.msmeCount} onChange={handleInputChange} placeholder="e.g. 15000" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Form.Item label="Large Industries Count" name="largeIndustriesCount">
                    <Input type="number" name="largeIndustriesCount" value={formData.largeIndustriesCount} onChange={handleInputChange} placeholder="e.g. 85" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item label="Major Industries Overview" name="majorIndustries">
                    <Input name="majorIndustries" value={formData.majorIndustries} onChange={handleInputChange} placeholder="Key sectors like IT, Textiles, Auto, etc." />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item label="Industrial Growth Rate (%)" name="industrialGrowthRate">
                    <Input type="number" name="industrialGrowthRate" value={formData.industrialGrowthRate} onChange={handleInputChange} placeholder="e.g. 7.5" />
                  </Form.Item>
                </Col>
                
                {/* ODOP Product Image Upload - Improved UI */}
                <Col xs={24}>
                  <Form.Item
                    label={
                      <Space>
                        <PictureOutlined style={{ color: '#470172' }} />
                        <span>ODOP Product Image</span>
                      </Space>
                    }
                    tooltip="Upload an image showcasing the district's signature product (One District One Product)"
                  >
                    <div style={{ 
                      border: '2px dashed #d9d9d9', 
                      borderRadius: 8, 
                      padding: 16,
                      background: '#fafafa',
                      transition: 'all 0.3s'
                    }}>
                      <Upload
                        listType="picture-card"
                        fileList={odopImageFileList}
                        onChange={handleOdopImageUpload}
                        beforeUpload={beforeOdopImageUpload}
                        maxCount={1}
                        accept="image/*"
                        onPreview={(file) => {
                          const url = file.url || (file.originFileObj ? URL.createObjectURL(file.originFileObj) : null);
                          if (url) window.open(url, '_blank');
                        }}
                        style={{ display: 'inline-block' }}
                      >
                        {odopImageFileList.length === 0 && (
                          <div style={{ 
                            display: 'flex', 
                            flexDirection: 'column', 
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '12px 0'
                          }}>
                            <PlusOutlined style={{ 
                              fontSize: 24, 
                              color: '#470172', 
                              marginBottom: 8 
                            }} />
                            <div style={{ 
                              fontSize: 13, 
                              fontWeight: 500, 
                              color: '#470172',
                              marginBottom: 4 
                            }}>
                              Upload Image
                            </div>
                            <div style={{ 
                              fontSize: 11, 
                              color: '#8c8c8c' 
                            }}>
                              Max 5MB
                            </div>
                          </div>
                        )}
                      </Upload>
                      {odopImageFileList.length === 0 && (
                        <div style={{ 
                          display: 'inline-block',
                          verticalAlign: 'top',
                          marginLeft: 16,
                          paddingTop: 8
                        }}>
                          <Text type="secondary" style={{ fontSize: 13, display: 'block', marginBottom: 4 }}>
                            Recommended: High-quality product image
                          </Text>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            Formats: JPG, PNG, JPEG • Max size: 5MB
                          </Text>
                        </div>
                      )}
                    </div>
                  </Form.Item>
                </Col>
              </Row>
            </Card>

            {/* SECTION 3: Natural Resources */}
            <Card bordered style={{ marginBottom: 18 }} headStyle={{ fontWeight: 600 }}>
              <SectionHeader icon={<EnvironmentOutlined />} title="Natural Resources" />
              <Row gutter={GUTTER}>
                {['Minerals', 'Agriculture Products', 'Forest Resources', 'Water Resources'].map((item) => {
                  const field = item.toLowerCase().replace(' ', '').replace('products', 'Products').replace('resources', 'Resources');
                  return (
                    <Col span={12} key={field}>
                      <Form.Item label={item} name={field}>
                        <Input name={field} value={formData[field]} onChange={handleInputChange} placeholder={`Key ${item.toLowerCase()}...`} />
                      </Form.Item>
                    </Col>
                  );
                })}
              </Row>
            </Card>

            {/* SECTION 4: Land & Zones */}
            <Card bordered style={{ marginBottom: 18 }} headStyle={{ fontWeight: 600 }}>
              <SectionHeader icon={<BankOutlined />} title="Land & Zones" />
              <Form.Item label="MIDC Zones (List)" name="midcZones">
                <Space.Compact style={{ width: '100%' }}>
                  <Input value={tempMidc} onChange={e => setTempMidc(e.target.value)} placeholder="Add Zone Name (e.g., Butibori MIDC)" />
                  <Button type="primary" onClick={addMidcZone} icon={<PlusOutlined />} />
                </Space.Compact>
                <div style={{ marginTop: 10, minHeight: 40, border: '1px solid #f0f0f0', padding: 8, borderRadius: 6 }}>
                  {formData.midcZones.map((zone, idx) => (
                    <Tag closable onClose={() => removeMidcZone(idx)} key={idx} color="purple" style={{ marginBottom: 4, marginRight: 8 }}>{zone}</Tag>
                  ))}
                  {formData.midcZones.length === 0 && <Text type="secondary">No zones added</Text>}
                </div>
              </Form.Item>
              <Form.Item label="Industrial Land/Shedding Profile" name="infrastructureDetails">
                <TextArea rows={2} name="infrastructureDetails" value={formData.infrastructureDetails} onChange={handleInputChange} placeholder="Details on land availability, plug-and-play facilities, etc." />
              </Form.Item>
            </Card>

            {/* SECTION 5: Logistics & Connectivity */}
            <Card bordered style={{ marginBottom: 18 }} headStyle={{ fontWeight: 600 }}>
              <SectionHeader icon={<CarOutlined />} title="Logistics & Connectivity" />
              <Row gutter={GUTTER}>
                <Col span={12}><Form.Item label="Major Highways" name="highways"><Input name="highways" value={formData.highways} onChange={handleInputChange} placeholder="NH 7, AH 43, etc." /></Form.Item></Col>
                <Col span={12}>
                  <Form.Item label="Railways" name="railways">
                    <Select value={formData.railways} onChange={v => handleSelectChange('railways', v)}>
                      <Option value="None">None</Option><Option value="Passenger">Passenger</Option><Option value="Freight">Freight</Option><Option value="Both">Both</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}><Form.Item label="Nearest Airport" name="nearestAirport"><Input name="nearestAirport" value={formData.nearestAirport} onChange={handleInputChange} placeholder="e.g. Dr. Babasaheb Ambedkar International Airport" /></Form.Item></Col>
                <Col span={12}><Form.Item label="Nearest Port" name="nearestPort"><Input name="nearestPort" value={formData.nearestPort} onChange={handleInputChange} placeholder="e.g. Jawaharlal Nehru Port" /></Form.Item></Col>
              </Row>
              <Form.Item label="Connectivity Details" name="connectivityDetails">
                <TextArea rows={1} name="connectivityDetails" value={formData.connectivityDetails} onChange={handleInputChange} placeholder="Fiber optic connectivity, road quality, etc." />
              </Form.Item>
            </Card>

            {/* SECTION 6: Power & Utilities */}
            <Card bordered style={{ marginBottom: 18 }} headStyle={{ fontWeight: 600 }}>
              <SectionHeader icon={<ThunderboltOutlined />} title="Power & Utilities" />
              <Row gutter={GUTTER}>
                <Col span={8}>
                  <Form.Item label="Power Status" name="powerAvailability">
                    <Select value={formData.powerAvailability} onChange={v => handleSelectChange('powerAvailability', v)}>
                   <Option value="Excellent">Excellent</Option>   <Option value="Deficit">Deficit</Option><Option value="Adequate">Adequate</Option><Option value="Surplus">Surplus</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={8}><Form.Item label="Water Supply" name="waterDetails"><Input name="waterDetails" value={formData.waterDetails} onChange={handleInputChange} placeholder="Source and capacity" /></Form.Item></Col>
                <Col span={8}><Form.Item label="Gas (PNG)" name="gasInfrastructure"><Input name="gasInfrastructure" value={formData.gasInfrastructure} onChange={handleInputChange} placeholder="Yes/No/Details" /></Form.Item></Col>
                <Col span={24}><Form.Item label="Power Details" name="powerDetails"><Input name="powerDetails" value={formData.powerDetails} onChange={handleInputChange} placeholder="Tariff, transmission capacity, etc." /></Form.Item></Col>
              </Row>
            </Card>

            {/* SECTION 7: Talent Ecosystem */}
            <Card bordered style={{ marginBottom: 18 }} headStyle={{ fontWeight: 600 }}>
              <SectionHeader icon={<ReadOutlined />} title="Talent Ecosystem" />
              <Row gutter={GUTTER}>
                <Col span={12}><Form.Item label="Skilled Labor Availability" name="skilledLabor"><Input name="skilledLabor" value={formData.skilledLabor} onChange={handleInputChange} placeholder="High/Medium/Low" /></Form.Item></Col>
                <Col span={12}><Form.Item label="Fire Safety Profile" name="fireSafetyProfile"><Input name="fireSafetyProfile" value={formData.fireSafetyProfile} onChange={handleInputChange} placeholder="Compliance rating" /></Form.Item></Col>
                <Col span={24}><Form.Item label="Education Hubs" name="educationDetails"><TextArea rows={1} name="educationDetails" value={formData.educationDetails} onChange={handleInputChange} placeholder="Universities, ITIs, etc." /></Form.Item></Col>
              </Row>
            </Card>

            {/* SECTION 8: Incentives & Subsidies */}
            <Card bordered style={{ marginBottom: 18 }} headStyle={{ fontWeight: 600 }}>
              <SectionHeader icon={<SafetyCertificateOutlined />} title="Key Incentives" />
              <Row gutter={24}>
                {['landSubsidy', 'stampDutyExemption', 'powerSubsidy', 'taxHoliday'].map(key => (
                  <Col span={6} key={key}>
                    <Form.Item style={{ marginBottom: 0 }}>
                      <Checkbox name={key} checked={formData[key]} onChange={handleInputChange}>
                        {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.charAt(0).toUpperCase() + str.slice(1))}
                      </Checkbox>
                    </Form.Item>
                  </Col>
                ))}
              </Row>
              <Divider style={{ margin: '20px 0' }} />
              <Form.Item label="Other Specific Incentives/Notes" name="otherIncentives">
                <Input name="otherIncentives" value={formData.otherIncentives} onChange={handleInputChange} placeholder="Specify any unique local or state incentives..." />
              </Form.Item>
            </Card>

            {/* SECTION 9: Investment Opportunities */}
            <Card bordered style={{ marginBottom: 18 }} headStyle={{ fontWeight: 600 }}>
              <SectionHeader icon={<ThunderboltOutlined />} title="Investment Opportunities" />
              <Form.Item label="Add New Opportunity">
                <Row gutter={16} align="middle">
                  <Col span={7}><Input placeholder="Sector (e.g., Electric Vehicles)" value={tempOpp.sector} onChange={e => setTempOpp({ ...tempOpp, sector: e.target.value })} /></Col>
                  <Col span={13}><Input placeholder="Rationale / Description (Why invest in this sector here?)" value={tempOpp.rationale} onChange={e => setTempOpp({ ...tempOpp, rationale: e.target.value })} /></Col>
                  <Col span={4}><Button type="primary" block onClick={addOpportunity} icon={<PlusOutlined />}>Add</Button></Col>
                </Row>
              </Form.Item>
              <List
                size="large"
                bordered
                dataSource={formData.investmentOpportunities}
                renderItem={(item, index) => (
                  <List.Item actions={[<Button type="text" danger icon={<DeleteOutlined />} onClick={() => removeOpportunity(index)} />]}>
                    <List.Item.Meta
                      avatar={<Avatar style={{ backgroundColor: '#f5222d', color: '#fff' }}>{item.sector[0]}</Avatar>}
                      title={<Text strong>{item.sector}</Text>}
                      description={item.rationale}
                    />
                  </List.Item>
                )}
              />
            </Card>

            {/* SECTION 10: Conveners/Co-Conveners */}
            <Card bordered style={{ marginBottom: 18 }} headStyle={{ fontWeight: 600 }}>
              <SectionHeader icon={<BankOutlined />} title="Conveners & Co-Conveners" />
              <Form.Item label="Add Convener/Co-Convener">
                <div style={{ backgroundColor: '#f0f5ff', padding: 16, borderRadius: 8 }}>
                  <Row gutter={12}>
                    <Col span={6}>
                      <Input 
                        placeholder="Full Name" 
                        value={tempChair.name} 
                        onChange={e => setTempChair({ ...tempChair, name: e.target.value })} 
                      />
                    </Col>
                    <Col span={5}>
                      <Input 
                        placeholder="Post/Designation" 
                        value={tempChair.postName} 
                        onChange={e => setTempChair({ ...tempChair, postName: e.target.value })} 
                      />
                    </Col>
                    <Col span={4}>
                      <Select 
                        style={{ width: '100%' }} 
                        value={tempChair.role} 
                        onChange={v => setTempChair({ ...tempChair, role: v })}
                      >
                        <Option value="Convener">Convener</Option>
                        <Option value="Co-Convener">Co-Convener</Option>
                        <Option value="Member">Member</Option>
                      </Select>
                    </Col>
                    <Col span={5}>
                      <Upload 
                        beforeUpload={f => handleFileChange({ file: f }, (file) => setTempChair({ ...tempChair, photo: file }))} 
                        maxCount={1} 
                        showUploadList={false}
                        accept="image/jpeg,image/png,image/jpg"
                      >
                        <Button block icon={<UploadOutlined />}>
                          {tempChair.photo ? 'Photo Set' : 'Upload Photo'}
                        </Button>
                      </Upload>
                    </Col>
                    <Col span={4}>
                      <Button 
                        type="primary" 
                        block 
                        onClick={() => addItem(
                          tempChair, 
                          setTempChair, 
                          'chairCoChairs', 
                          { name: '', description: '', postName: '', role: 'Convener', photo: null }
                        )}
                      >
                        Add Person
                      </Button>
                    </Col>
                    <Col span={24} style={{ marginTop: 8 }}>
                      <TextArea 
                        rows={2}
                        placeholder="Brief description or bio (optional)" 
                        value={tempChair.description} 
                        onChange={e => setTempChair({ ...tempChair, description: e.target.value })} 
                      />
                    </Col>
                  </Row>
                </div>
              </Form.Item>
              <List
                grid={{ gutter: 24, column: 3 }}
                dataSource={formData.chairCoChairs}
                locale={{
                  emptyText: (
                    <div style={{ padding: '40px 0', textAlign: 'center', color: '#999' }}>
                      <Users size={48} style={{ marginBottom: 16, opacity: 0.3 }} />
                      <div>No chair/co-chair members added yet</div>
                    </div>
                  )
                }}
                renderItem={(item, index) => (
                  <List.Item>
                    <Card 
                      size="small" 
                      hoverable 
                      actions={[
                        <DeleteOutlined 
                          key="del" 
                          onClick={() => removeItem('chairCoChairs', index)} 
                          style={{ color: 'red' }} 
                        />
                      ]}
                      style={{ textAlign: 'center' }}
                    >
                      <div style={{ marginBottom: 12 }}>
                        {item.photo ? (
                          <Avatar 
                            size={80} 
                            src={item.photo instanceof File ? URL.createObjectURL(item.photo) : `${process.env.REACT_APP_API_BASE_URL}/uploads/${item.photo}`}
                            style={{ border: '3px solid #470172' }}
                          />
                        ) : (
                          <Avatar 
                            size={80} 
                            style={{ 
                              backgroundColor: '#470172', 
                              fontSize: 32,
                              border: '3px solid #470172'
                            }}
                          >
                            {item.name?.charAt(0)?.toUpperCase()}
                          </Avatar>
                        )}
                      </div>
                      <List.Item.Meta
                        title={
                          <div>
                            <Text strong style={{ fontSize: 16 }}>{item.name}</Text>
                            <div style={{ marginTop: 4 }}>
                              <Tag color={(item.role === 'Chair' || item.role === 'Convener') ? 'purple' : 'blue'} style={{ fontSize: 12 }}>
                                {item.role === 'Chair' ? 'Convener' : item.role === 'Co-Chair' ? 'Co-Convener' : item.role}
                              </Tag>
                            </div>
                          </div>
                        }
                        description={
                          <div style={{ marginTop: 8 }}>
                            <div style={{ fontSize: 13, color: '#666', marginBottom: 4 }}>
                              {item.postName}
                            </div>
                            {item.description && (
                              <div style={{ fontSize: 12, color: '#999', marginTop: 8 }}>
                                {item.description.length > 80 
                                  ? `${item.description.substring(0, 80)}...` 
                                  : item.description}
                              </div>
                            )}
                          </div>
                        }
                      />
                    </Card>
                  </List.Item>
                )}
              />
            </Card>

            {/* SECTION 11: Policies */}
            <Card bordered title={<Space><FilePdfOutlined /> Policies</Space>} size="small" headStyle={{ fontWeight: 600 }} style={{ marginBottom: 18 }} extra={
              <Button size="small" type="primary" ghost onClick={() => addItem(tempPolicy, setTempPolicy, 'policies', { name: '', description: '', date: '', file: null })} icon={<PlusOutlined />}>Add Policy</Button>
            }>
              <Space direction="vertical" style={{ width: '100%', marginBottom: 16 }}>
                <Input placeholder="Policy Name" value={tempPolicy.name} onChange={e => setTempPolicy({ ...tempPolicy, name: e.target.value })} />
                <Input placeholder="Description" value={tempPolicy.description} onChange={e => setTempPolicy({ ...tempPolicy, description: e.target.value })} />
                <Row gutter={8}>
                  <Col span={12}><Input type="date" value={tempPolicy.date} onChange={e => setTempPolicy({ ...tempPolicy, date: e.target.value })} /></Col>
                  <Col span={12}>
                    <Upload beforeUpload={f => handleFileChange({ file: f }, (file) => setTempPolicy({ ...tempPolicy, file }))} maxCount={1} showUploadList={false}>
                      <Button block icon={<UploadOutlined />}>{tempPolicy.file ? `File: ${tempPolicy.file.name.substring(0, 10)}...` : 'Upload PDF'}</Button>
                    </Upload>
                  </Col>
                </Row>
              </Space>
              <List size="small" dataSource={formData.policies} renderItem={(item, i) => (
                <List.Item actions={[<DeleteOutlined onClick={() => removeItem('policies', i)} style={{ color: 'red' }} />]}>
                  <Space><FilePdfOutlined style={{ color: '#5b2a86' }} /> <Text>{item.name}</Text></Space>
                </List.Item>
              )} />
            </Card>

            {/* SECTION 12: Schemes */}
            <Card bordered title={<Space><ReadOutlined /> Schemes</Space>} size="small" headStyle={{ fontWeight: 600 }} style={{ marginBottom: 18 }} extra={
              <Button size="small" type="primary" ghost onClick={() => addItem(tempScheme, setTempScheme, 'schemes', { name: '', description: '', date: '', file: null })} icon={<PlusOutlined />}>Add Scheme</Button>
            }>
              <Space direction="vertical" style={{ width: '100%', marginBottom: 16 }}>
                <Input placeholder="Scheme Name" value={tempScheme.name} onChange={e => setTempScheme({ ...tempScheme, name: e.target.value })} />
                <Input placeholder="Description" value={tempScheme.description} onChange={e => setTempScheme({ ...tempScheme, description: e.target.value })} />
                <Row gutter={8}>
                  <Col span={12}><Input type="date" value={tempScheme.date} onChange={e => setTempScheme({ ...tempScheme, date: e.target.value })} /></Col>
                  <Col span={12}>
                    <Upload beforeUpload={f => handleFileChange({ file: f }, (file) => setTempScheme({ ...tempScheme, file }))} maxCount={1} showUploadList={false}>
                      <Button block icon={<UploadOutlined />}>{tempScheme.file ? `File: ${tempScheme.file.name.substring(0, 10)}...` : 'Upload PDF'}</Button>
                    </Upload>
                  </Col>
                </Row>
              </Space>
              <List size="small" dataSource={formData.schemes} renderItem={(item, i) => (
                <List.Item actions={[<DeleteOutlined onClick={() => removeItem('schemes', i)} style={{ color: 'red' }} />]}>
                  <Space><ReadOutlined style={{ color: '#5b2a86' }} /> <Text>{item.name}</Text></Space>
                </List.Item>
              )} />
            </Card>

            {/* SECTION 13: Media Coverage */}
            <Card bordered title={<Space><CameraOutlined /> Media Coverage</Space>} size="small" style={{ marginBottom: 18 }}>
              <Form.Item label="Add Media Article">
                <Space direction="vertical" style={{ width: '100%', marginBottom: 8 }}>
                  <Row gutter={8}>
                    <Col span={12}><Input placeholder="Article Title" value={tempMedia.title} onChange={e => setTempMedia({ ...tempMedia, title: e.target.value })} /></Col>
                    <Col span={12}><Input placeholder="Source Name (e.g., Times of India)" value={tempMedia.sourceName} onChange={e => setTempMedia({ ...tempMedia, sourceName: e.target.value })} /></Col>
                    <Col span={12} style={{ marginTop: 8 }}><Input placeholder="Source URL (https://...)" prefix={<LinkOutlined style={{ color: '#bfbfbf' }} />} value={tempMedia.sourceUrl} onChange={e => setTempMedia({ ...tempMedia, sourceUrl: e.target.value })} /></Col>
                    <Col span={12} style={{ marginTop: 8 }}><Input type="date" value={tempMedia.date} onChange={e => setTempMedia({ ...tempMedia, date: e.target.value })} /></Col>
                    <Col span={12} style={{ marginTop: 8 }}>
                      <Upload beforeUpload={f => handleFileChange({ file: f }, (file) => setTempMedia({ ...tempMedia, image: file }))} maxCount={1} showUploadList={false} accept="image/jpeg,image/png,image/jpg">
                        <Button block icon={<UploadOutlined />}>{tempMedia.image ? 'Image Set' : 'Upload Image'}</Button>
                      </Upload>
                    </Col>
                    <Col span={12} style={{ marginTop: 8 }}><Input placeholder="Short Description" value={tempMedia.description} onChange={e => setTempMedia({ ...tempMedia, description: e.target.value })} /></Col>
                    <Col span={24} style={{ marginTop: 8 }}><Button block type="primary" onClick={() => addItem(tempMedia, setTempMedia, 'mediaCoverage', { title: '', description: '', date: '', sourceName: '', sourceUrl: '', image: null })}>Add Article</Button></Col>
                  </Row>
                </Space>
              </Form.Item>
              <List
                itemLayout="horizontal"
                size="small"
                dataSource={formData.mediaCoverage}
                renderItem={(item, index) => (
                  <List.Item actions={[<DeleteOutlined onClick={() => removeItem('mediaCoverage', index)} style={{ color: 'red' }} />]}>
                    <List.Item.Meta 
                      title={
                        <div>
                          <Text strong style={{ fontSize: 14 }}>{item.title}</Text>
                          {item.sourceName && (
                            <Tag color="blue" style={{ marginLeft: 8, fontSize: 12 }}>
                              {item.sourceName}
                            </Tag>
                          )}
                        </div>
                      }
                      description={
                        <div style={{ marginTop: 4 }}>
                          {item.sourceUrl && (
                            <div style={{ marginBottom: 4 }}>
                              <LinkOutlined style={{ marginRight: 6, color: '#1890ff' }} />
                              <a href={item.sourceUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: '#1890ff' }}>
                                {item.sourceUrl}
                              </a>
                            </div>
                          )}
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {item.date && `Published: ${item.date}`}
                          </Text>
                          {item.description && (
                            <div style={{ marginTop: 4, fontSize: 13, color: '#666' }}>
                              {item.description}
                            </div>
                          )}
                        </div>
                      }
                    />
                  </List.Item>
                )}
              />
            </Card>

            {/* SECTION 14: Press Releases */}
            <Card bordered title={<Space><FileTextOutlined /> Press Releases</Space>} size="small" style={{ marginBottom: 18 }}>
              <Form.Item label="Add Press Release">
                <Space direction="vertical" style={{ width: '100%', marginBottom: 8 }}>
                  <Input placeholder="Title" value={tempPress.title} onChange={e => setTempPress({ ...tempPress, title: e.target.value })} />
                  <Row gutter={8}>
                    <Col span={12}><Input placeholder="Source/Department" value={tempPress.source} onChange={e => setTempPress({ ...tempPress, source: e.target.value })} /></Col>
                    <Col span={12}>
                      <Upload beforeUpload={f => handleFileChange({ file: f }, (file) => setTempPress({ ...tempPress, file }))} maxCount={1} showUploadList={false}>
                        <Button block icon={<UploadOutlined />}>{tempPress.file ? 'PDF Set' : 'Upload PDF'}</Button>
                      </Upload>
                    </Col>
                  </Row>
                  <Button block type="primary" onClick={() => addItem(tempPress, setTempPress, 'pressReleases', { title: '', description: '', date: '', source: '', file: null })}>Add Release</Button>
                </Space>
              </Form.Item>
              <List
                size="small"
                dataSource={formData.pressReleases}
                renderItem={(item, i) => (
                  <List.Item actions={[<DeleteOutlined onClick={() => removeItem('pressReleases', i)} style={{ color: 'red' }} />]}>
                    <Space><FilePdfOutlined style={{ color: '#5b2a86' }} /> <Text>{item.title}</Text> - <Text type="secondary">{item.source}</Text></Space>
                  </List.Item>
                )}
              />
            </Card>

            {/* SECTION 15: Awards & Recognition */}
            <Card bordered style={{ marginBottom: 18 }} headStyle={{ fontWeight: 600 }}>
              <SectionHeader icon={<TrophyOutlined />} title="Awards & Recognition" />
              <Form.Item label="Add New Award">
                <div style={{ backgroundColor: '#f9f0ff', padding: 16, borderRadius: 8 }}>
                  <Row gutter={12}>
                    <Col span={5}><Input placeholder="Award Name" value={tempAward.awardName} onChange={e => setTempAward({ ...tempAward, awardName: e.target.value })} /></Col>
                    <Col span={5}><Input placeholder="Organization" value={tempAward.organization} onChange={e => setTempAward({ ...tempAward, organization: e.target.value })} /></Col>
                    <Col span={5}>
                      <Select style={{ width: '100%' }} value={tempAward.category} onChange={v => setTempAward({ ...tempAward, category: v })}>
                        <Option value="Industrial Development">Industrial Dev</Option><Option value="Innovation">Innovation</Option><Option value="Sustainability">Sustainability</Option>
                      </Select>
                    </Col>
                    <Col span={5}>
                      <Upload beforeUpload={f => handleFileChange({ file: f }, (file) => setTempAward({ ...tempAward, certificate: file }))} maxCount={1} showUploadList={false}>
                        <Button block icon={<UploadOutlined />}>{tempAward.certificate ? 'Cert Set' : 'Upload Certificate'}</Button>
                      </Upload>
                    </Col>
                    <Col span={4}>
                      <Button type="primary" block onClick={() => addItem(tempAward, setTempAward, 'awards', { awardName: '', description: '', organization: '', category: 'Industrial Development', date: '', certificate: null })}>Add Award</Button>
                    </Col>
                    <Col span={24} style={{ marginTop: 8 }}><Input placeholder="Brief Description of the achievement" value={tempAward.description} onChange={e => setTempAward({ ...tempAward, description: e.target.value })} /></Col>
                  </Row>
                </div>
              </Form.Item>
              <List
                grid={{ gutter: 24, column: 3 }}
                dataSource={formData.awards}
                renderItem={(item, index) => (
                  <List.Item>
                    <Card size="small" hoverable actions={[<DeleteOutlined key="del" onClick={() => removeItem('awards', index)} style={{ color: 'red' }} />]}>
                      <List.Item.Meta
                        avatar={<TrophyOutlined style={{ fontSize: 24, color: '#470172' }} />}
                        title={<Text strong>{item.awardName}</Text>}
                        description={
                          <div>
                            <Tag color="geekblue" style={{ marginBottom: 4 }}>{item.category}</Tag>
                            <div style={{ fontSize: 12 }}>{item.organization}</div>
                          </div>
                        }
                      />
                    </Card>
                  </List.Item>
                )}
              />
            </Card>

            {/* SECTION 15: Resources (Downloads) */}
            <Card bordered style={{ marginBottom: 18 }} headStyle={{ fontWeight: 600 }}>
              <SectionHeader icon={<FileTextOutlined />} title="Resources & Downloads" />
              <Form.Item label="Add New Resource">
                <div style={{ padding: 12, borderRadius: 8, background: '#fafafa' }}>
                  <Row gutter={GUTTER} align="middle">
                    <Col span={8}><Input placeholder="Resource Name (e.g., Investor Guide)" value={tempResource.name} onChange={e => setTempResource({ ...tempResource, name: e.target.value })} /></Col>
                    <Col span={8}><Input placeholder="Description" value={tempResource.description} onChange={e => setTempResource({ ...tempResource, description: e.target.value })} /></Col>
                    <Col span={4}>
                      <Upload
                        beforeUpload={f => handleFileChange({ file: f }, (file) => setTempResource({ ...tempResource, document: file }))}
                        maxCount={1}
                        showUploadList={false}
                        accept=".pdf"
                      >
                        <Button block icon={<UploadOutlined />}>{tempResource.document ? 'File Set' : 'Upload PDF'}</Button>
                      </Upload>
                    </Col>
                    <Col span={4}>
                      <Button type="primary" block onClick={() => addItem(tempResource, setTempResource, 'resources', { name: '', description: '', date: '', document: null })}>
                        Add Resource
                      </Button>
                    </Col>
                  </Row>
                </div>
              </Form.Item>
              <List
                size="large"
                dataSource={formData.resources}
                renderItem={(item, index) => (
                  <List.Item actions={[<DeleteOutlined onClick={() => removeItem('resources', index)} style={{ color: 'red' }} />]}>
                    <List.Item.Meta
                      avatar={<FileTextOutlined style={{ color: '#470172', fontSize: 22 }} />}
                      title={<Text strong>{item.name}</Text>}
                      description={item.description}
                    />
                  </List.Item>
                )}
              />

            </Card>

            {/* Removed duplicated Save / Cancel bar (modal footer will handle actions) */}
          </Form>
        </div>
      </div>


    </ConfigProvider>
  );
};

export default AddDistrictForm;